/**
 * Mirinae Platform Sanitizer Utility
 * 
 * STANDARD: v1.0 (Based on DaVinciSanitizer v1.36.69)
 * 
 * Responsible for cleaning HTML content platform-wide to prevent XSS attacks 
 * while allowing specific Rich Text styling tags.
 * 
 * Rules:
 * 1. Allow whitelisted tags: b, strong, i, em, u, s, span, br, p, div, font
 * 2. Allow whitelisted attributes: style, class, color
 * 3. Strip all scripts, iframes, objects, and event handlers.
 */

export const MirinaeSanitizer = {
    // Whitelist configuration
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 's', 'span', 'br', 'p', 'div', 'font', 'img'],
    ALLOWED_ATTRS: ['style', 'class', 'color', 'src', 'alt'],

    /**
     * Sanitizes a HTML string.
     * @param {string} html - Potentially unsafe HTML string
     * @returns {string} - Cleaned HTML string
     */
    sanitize(html) {
        if (!html) return '';

        // 1. Create a dummy document to parse the HTML safely
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const body = doc.body;

        // 2. Recursively clean nodes
        this._cleanNode(body);

        return body.innerHTML;
    },

    /**
     * Internal recursive cleaner
     * @param {Node} node 
     */
    _cleanNode(node) {
        let child = node.firstChild;

        while (child) {
            const next = child.nextSibling;

            if (child.nodeType === Node.ELEMENT_NODE) {
                const tagName = child.tagName.toLowerCase();

                // Rule A: Remove script/iframe/object entirely
                if (['script', 'iframe', 'object', 'embed', 'style', 'link', 'meta'].includes(tagName)) {
                    node.removeChild(child);
                }
                // Rule B: Unwrap banned tags (keep text, remove tag)
                else if (!this.ALLOWED_TAGS.includes(tagName)) {
                    // Create a fragment with child's children
                    while (child.firstChild) {
                        node.insertBefore(child.firstChild, child);
                    }
                    node.removeChild(child);
                }
                // Rule C: Clean attributes for allowed tags
                else {
                    // Remove all attributes first, then restore allowed ones
                    const attrs = Array.from(child.attributes);
                    for (const attr of attrs) {
                        const name = attr.name.toLowerCase();
                        // Allow specific attributes
                        if (!this.ALLOWED_ATTRS.includes(name)) {
                            child.removeAttribute(name);
                        }
                        // Security: Check style for 'javascript:' or 'url('
                        if (name === 'style') {
                            const val = attr.value.toLowerCase();
                            if (val.includes('javascript:') || val.includes('url(') || val.includes('expression(')) {
                                child.removeAttribute('style');
                            }
                        }
                        // Security: Check src for 'javascript:'
                        if (name === 'src') {
                            const val = attr.value.toLowerCase();
                            if (val.includes('javascript:')) {
                                child.removeAttribute('src');
                            }
                        }

                        // [FIX] 2026-02-15 Strip Editor Dark Mode Colors AND Font Sizes
                        // Identify if 'style' contains specific Zinc-300 or White colors that break White Theme
                        // Also strip font-size to ensure consistency.
                        if (name === 'style') {
                            let styleVal = attr.value;
                            // Regex for Zinc-300 (rgb(212, 212, 216)) and White
                            styleVal = styleVal.replace(/color:\s*rgb\(212,\s*212,\s*216\);?/gi, '');
                            styleVal = styleVal.replace(/color:\s*white;?/gi, '');
                            styleVal = styleVal.replace(/color:\s*#ffffff;?/gi, '');

                            // [NEW] Strip font-size
                            styleVal = styleVal.replace(/font-size:\s*[^;]+;?/gi, '');

                            if (styleVal !== attr.value) {
                                if (styleVal.trim() === '' || styleVal.trim() === ';') {
                                    child.removeAttribute('style');
                                } else {
                                    child.setAttribute('style', styleVal);
                                }
                            }
                        }
                    }
                }

                // [Feature] Auto-Dialogue Class Injection for Rich Text
                // If block-level element (p, div) starts with Quote, add 'type-dialogue' class.
                // [FIX] Prevent Recursive Nesting (Double Blue Lines)
                if (['p', 'div'].includes(tagName)) {
                    const hasBlockChildren = Array.from(child.children).some(c =>
                        ['div', 'p'].includes(c.tagName.toLowerCase())
                    );

                    if (!hasBlockChildren) {
                        const text = child.textContent.trim();
                        if (text.startsWith('"') || text.startsWith('“')) {
                            child.classList.add('type-dialogue');
                            // Ensure basic paragraph class exists for styling consistency
                            // Only add if not already present
                            if (!child.classList.contains('dv-paragraph')) {
                                child.classList.add('dv-paragraph');
                            }
                        }
                    }
                }

                // Recurse
                this._cleanNode(child);
            } else if (child.nodeType === Node.TEXT_NODE) {
                // Keep text defined
            } else {
                // Remove comments, etc.
                node.removeChild(child);
            }

            child = next;
        }
    },

    /**
     * Converts legacy plain text (User typed \n) to HTML
     * @param {string} text 
     * @returns {string}
     */
    convertLegacyToHtml(text) {
        if (!text) return '';
        // Escape HTML entities first to prevent injection from old text
        const escaped = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

        // Convert newlines to <br>
        return escaped.replace(/\n/g, '<br>');
    },

    /**
     * Detects if content mimics HTML
     * @param {string} content 
     * @returns {boolean}
     */
    isHtml(content) {
        if (!content) return false;
        // Relaxed Check: ContentEditable creates tags anywhere.
        return /<(div|p|span|br|b|i|u|s|strong|em|img|font)\b/i.test(content);
    }
};
